import { useGetPartnerList, usePartnerInfo } from "@/api/services/main/partner";
import { IconNavigation } from "@/assets/icons";
import { Checkbox } from "@/components/elements/Checkbox";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { SelectAsync } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useVisible } from "@/hooks/useVisible";
import { divider, black } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { Stack, Box, Typography, Grid, CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useRef, useMemo, useState } from "react";
import { convertNumberToZipCode } from "@/utils/input";
import { findLabelByValue } from "@/utils/object";
import { useEnum } from "@/api/services/global/enum";
import { JAPAN_NATIONALITY_ID } from "@/types/enum";
import { AppRoutes } from "@/routes/config";
import { queryClient } from "@/constants/queryClient";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { getDynamicRoute } from "@/utils/url";

export const TalentFormPartnerInfo: FCC = () => {
  const methods = useFormContext<TTalentForm>();
  const { control, getValues } = methods;
  const { data: contactMethodOptions } = useEnum("EPartnerContactMethod");

  const partnerId = useWatch({ name: "partnerId" });

  const { data: partnerInfo, isLoading: isPartnerLoading } = usePartnerInfo(partnerId);

  const defaultPartner = getValues("partner");

  const partner = partnerId ? partnerInfo?.data : defaultPartner;

  // Only the INITIALLY-saved partner (captured once at mount) is sent to the list query, so the BE can
  // include it on page 1. Using the live `partnerId` here would refetch the whole list on every selection
  // (the picked partner is already in the loaded list — no need to re-call). Selection still updates the
  // label via `selectedOption` below.
  const [initialPartnerId] = useState<number | null>(() => {
    const saved = getValues("partnerId");
    return saved ? Number(saved) : null;
  });

  // Lazy-load partners (50/page) via GET /partners.
  const {
    options: partnerPageOptions,
    fetchNextPage,
    hasNextPage,
    isFetching: isPartnerFetching,
    isFetchingNextPage,
  } = useGetPartnerList("", initialPartnerId);
  // Numeric values to match the numeric `partnerId` form field
  const partnerOptions = useMemo(() => partnerPageOptions.map((opt) => ({ label: opt.label, value: Number(opt.value) })), [partnerPageOptions]);
  // Keep the already-selected partner's label available even before its page is loaded
  const selectedPartnerOption = useMemo(
    () => (partnerId && partner?.partnerName ? { value: Number(partnerId), label: partner.partnerName } : null),
    [partnerId, partner],
  );

  const isLoadingNewPartner = isPartnerLoading && partnerId;

  const isNotJapan = Number(partner?.nationalityId) !== JAPAN_NATIONALITY_ID;

  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  const menuVisible = useVisible(false);

  // The partner create/edit pages open in a separate tab, so this form never learns about their
  // changes. Refresh the dropdown and the selected partner's details when the user comes back,
  // but only if they actually opened one of those tabs.
  const openedPartnerTabRef = useRef(false);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !openedPartnerTabRef.current) return;
      openedPartnerTabRef.current = false;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PARTNER.FETCH_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PARTNER.FETCH_DETAIL] });
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const handleNewPartner = () => {
    menuVisible.close();
    openedPartnerTabRef.current = true;
    window.open(AppRoutes.partnerCreate, "_blank", "noopener,noreferrer");
  };

  const handleEditPartner = () => {
    menuVisible.close();
    if (!partnerId) return;
    openedPartnerTabRef.current = true;
    window.open(getDynamicRoute(AppRoutes.partnerEdit, { partnerId }), "_blank", "noopener,noreferrer");
  };

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            パートナー情報
          </Typography>
        </Box>

        <IconButton ref={menuAnchorRef} onClick={menuVisible.open} size="small">
          <IconNavigation />
        </IconButton>
        <Menu
          anchorEl={menuAnchorRef.current}
          open={menuVisible.visible}
          onClose={menuVisible.close}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleNewPartner}>パートナー新規登録</MenuItem>
          {partnerId && <MenuItem onClick={handleEditPartner}>パートナー情報を編集</MenuItem>}
        </Menu>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FormField control={control} name="partnerId">
              <SelectAsync
                options={partnerOptions}
                selectedOption={selectedPartnerOption}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetching={isPartnerFetching}
                isFetchingNextPage={isFetchingNextPage}
              />
            </FormField>
          </Grid>

          {partnerId && partner && (
            <>
              {isLoadingNewPartner && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "20px",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                </Grid>
              )}

              {partner?.sendingOrganization && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderRadius: rounded.md,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1px solid ${black[95]}`,
                      color: black[30],
                      ...typography.cap12,
                      lineHeight: 1,
                      padding: "4px 10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    送り出し機関
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} mt={1}>
                <FormField name="partnerManagementNumber">
                  <Input readonly value={isLoadingNewPartner ? "" : partner?.managementNumber} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerNationality" required>
                  <Input readonly value={isLoadingNewPartner ? "" : partner?.nationality?.nationalityName} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              {!isNotJapan && (
                <Grid item xs={12} mt={1}>
                  <FormField name="partnerZipCode">
                    <Input
                      readonly
                      value={isLoadingNewPartner ? "" : partner?.zipCode ? convertNumberToZipCode(partner.zipCode) : ""}
                      disabled={isLoadingNewPartner}
                    />
                  </FormField>
                </Grid>
              )}

              {!isNotJapan && (
                <Grid item xs={6} mt={1}>
                  <FormField name="partnerPrefecture">
                    <Input readonly value={isLoadingNewPartner ? "" : partner?.prefecture} disabled={isLoadingNewPartner} />
                  </FormField>
                </Grid>
              )}

              {!isNotJapan && (
                <Grid item xs={6} mt={1}>
                  <FormField name="partnerCity">
                    <Input readonly value={isLoadingNewPartner ? "" : partner?.city} disabled={isLoadingNewPartner} />
                  </FormField>
                </Grid>
              )}

              {!isNotJapan && (
                <Grid item xs={12} mt={1}>
                  <FormField name="partnerAddress">
                    <Input readonly value={isLoadingNewPartner ? "" : partner?.address} disabled={isLoadingNewPartner} />
                  </FormField>
                </Grid>
              )}

              {isNotJapan && (
                <Grid item xs={12} mt={1}>
                  <FormField name="partnerLocation">
                    <Input readonly value={isLoadingNewPartner ? "" : partner?.location} disabled={isLoadingNewPartner} />
                  </FormField>
                </Grid>
              )}

              <Grid item xs={12} mt={1}>
                <FormField name="partnerContactPerson">
                  <Input readonly value={isLoadingNewPartner ? "" : partner?.contactPerson} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerContactMethod">
                  <Input
                    readonly
                    value={isLoadingNewPartner ? "" : findLabelByValue(contactMethodOptions, partner?.contactMethod)}
                    disabled={isLoadingNewPartner}
                  />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerPhoneNumber">
                  <Input readonly value={isLoadingNewPartner ? "" : partner?.phoneNumber} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerEmail">
                  <Input readonly value={isLoadingNewPartner ? "" : partner?.email} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerOtherContacts">
                  <Input readonly value={isLoadingNewPartner ? "" : partner?.otherContacts} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerLaborBureauStatus">
                  <Checkbox readOnly={true} disabled={true} checked={!!partner?.laborBureauStatus} label="済" />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerContractInfo">
                  <Input readonly value={isLoadingNewPartner ? "" : partner?.contractInfo} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerBillingInfo">
                  <Input readonly value={isLoadingNewPartner ? "" : partner?.billingInfo} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerMemo">
                  <Textarea
                    readonly
                    value={isLoadingNewPartner ? "" : partnerId && partner?.memo?.length ? partner.memo : ""}
                    disabled={isLoadingNewPartner}
                  />
                </FormField>
              </Grid>
            </>
          )}
        </Grid>
      </Stack>
    </InnerBox>
  );
};
